import {
  css as styledComponentsCss,
  CSSObject,
  FlattenInterpolation,
  Interpolation,
  InterpolationFunction,
  ThemedStyledProps,
} from 'styled-components';
import type { Theme as TrellisTheme } from '../types';
import { getTheme } from './getTheme';

// Define an opaque type for Theme to compress error messages in TS
type Opaque<T, K extends string> = T & { __TYPE__: K };
export type Theme = Opaque<TrellisTheme, 'Theme'>;

/**
 * Enhanced replacement for styled-components' `css` function that automatically
 * injects the theme into style functions. This follows the same pattern
 * as `styledWithTheme` to ensure consistent theme access.
 *
 * @example
 * ```tsx
 * // With template literal
 * const textStyles = css`
 *   color: ${({ theme }) => theme.color['text-core-default']};
 * `;
 *
 * // With object styles
 * const textStyles = css(({ theme }) => ({
 *   color: theme.color['text-core-default'],
 * }));
 *
 * // With conditional styling based on props
 * const dynamicStyles = css<{ $isActive: boolean }>`
 *   color: ${({ theme, $isActive }) =>
 *     $isActive ? theme.color['text-primary'] : theme.color['text-secondary']};
 * `;
 * ```
 */
export function css<P extends object = {}>(
  first:
    | TemplateStringsArray
    | CSSObject
    | InterpolationFunction<ThemedStyledProps<P, Theme>>,
  ...interpolations: Array<Interpolation<ThemedStyledProps<P, Theme>>>
): FlattenInterpolation<ThemedStyledProps<P, Theme>> {
  // Handle template strings
  if (Array.isArray(first)) {
    // Process each interpolation to inject theme if it's a function
    const processedInterpolations = interpolations.map((interpolation) => {
      if (typeof interpolation === 'function') {
        return (props: any) => {
          const theme = getTheme({ theme: props.theme });

          return interpolation({ ...props, theme });
        };
      }

      return interpolation;
    });

    return styledComponentsCss(first, ...processedInterpolations);
  }

  // Handle style objects or functions
  if (typeof first === 'function') {
    return styledComponentsCss((props: any) => {
      const theme = getTheme({ theme: props.theme });

      return first({ ...props, theme });
    });
  }

  // Handle css object directly
  return styledComponentsCss(first);
}
